import React, { useState } from "react";
import { BadgeFormaPago, formatoMonto } from "../utils/estados";
import { Ayuda } from "../context/AyudaContext";

// Ventana de confirmación antes de aprobar: muestra, cotización por
// cotización, qué productos se van a aprobar, con qué proveedor y por cuánto,
// y avisa cuando el gerente eligió una cotización distinta a la recomendada.
// Cada producto se puede desmarcar — el gerente puede aprobar solo una parte
// de lo que trae una cotización y dejar el resto sin decidir todavía
// (compras tendrá que traer una cotización aparte para esos productos).
//
// grupos: [{ id, cotizacion, items: [{ id, descripcion, cantidad, unidad, precio }],
//            recomendada: cotizacion|null }]   (recomendada = la de compras,
//            solo cuando es distinta a la que se va a aprobar)
const simboloMoneda = (moneda) => (moneda === "USD" ? "US$" : "C$");
const dinero = (simbolo, n) => `${simbolo}${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ConfirmarAprobacionModal({ grupos, folio, nota, procesando, onCancelar, onConfirmar }) {
  // Por defecto se aprueba la cantidad completa de cada producto (mismo
  // comportamiento de antes); el gerente puede bajar la cantidad de
  // cualquiera (aprobación parcial) o ponerla en 0 (no aprobar todavía).
  const [cantidades, setCantidades] = useState(() => {
    const mapa = {};
    grupos.forEach((g) => g.items.forEach((it) => (mapa[it.id] = it.cantidad)));
    return mapa;
  });

  if (!grupos || grupos.length === 0) return null;

  const alternarItem = (itemId, cantidadTotal) => {
    setCantidades((prev) => ({ ...prev, [itemId]: prev[itemId] > 0 ? 0 : cantidadTotal }));
  };

  const cambiarCantidad = (itemId, valor, cantidadTotal) => {
    const numero = Math.round(Number(valor));
    const limitado = Number.isFinite(numero) ? Math.min(Math.max(numero, 0), cantidadTotal) : 0;
    setCantidades((prev) => ({ ...prev, [itemId]: limitado }));
  };

  const totalProductos = grupos.reduce((suma, g) => suma + g.items.length, 0);
  const totalSeleccionados = grupos.reduce(
    (suma, g) => suma + g.items.filter((it) => cantidades[it.id] > 0).length,
    0
  );

  const marcarTodos = () => {
    const mapa = {};
    grupos.forEach((g) => g.items.forEach((it) => (mapa[it.id] = it.cantidad)));
    setCantidades(mapa);
  };
  const quitarTodos = () => {
    const mapa = {};
    grupos.forEach((g) => g.items.forEach((it) => (mapa[it.id] = 0)));
    setCantidades(mapa);
  };
  const hayDistinta = grupos.some((g) => g.recomendada);

  // Si se bajó la cantidad de un producto, su precio se prorratea (mismo
  // criterio que usa el backend al aprobar) — es solo una vista previa.
  const subtotalItem = (it) => (Number(it.precio) * (cantidades[it.id] || 0)) / (it.cantidadBase ?? it.cantidad);

  const confirmar = () => {
    const seleccionPorGrupo = {};
    const excluidosInfo = [];
    grupos.forEach((g) => {
      const itemIds = g.items.filter((it) => cantidades[it.id] > 0).map((it) => it.id);
      const parciales = {};
      g.items.forEach((it) => {
        const cant = cantidades[it.id];
        if (cant > 0 && cant < it.cantidad) parciales[it.id] = cant;
      });
      seleccionPorGrupo[g.id] = { itemIds, cantidades: parciales };
      g.items
        .filter((it) => !(cantidades[it.id] > 0))
        .forEach((it) =>
          excluidosInfo.push({ id: it.id, descripcion: it.descripcion, cantidad: it.cantidad, unidad: it.unidad })
        );
    });
    onConfirmar(seleccionPorGrupo, excluidosInfo);
  };

  return (
    <div style={styles.overlay} onClick={procesando ? undefined : onCancelar}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.encabezado}>
          <div style={styles.icono}>✅</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={styles.titulo}>¿Estás seguro de aprobar esta compra?</h3>
            <Ayuda>
            <p style={styles.subtitulo}>
              Desmarca un producto o baja su cantidad si no quieres aprobarlo todavía.
            </p>
            </Ayuda>
          </div>
          <div style={styles.resumenChips}>
            {folio && <span style={styles.chipFolio}>Solicitud {folio}</span>}
            <span style={styles.chipConteo}>
              {totalSeleccionados === totalProductos
                ? `${totalProductos} producto${totalProductos === 1 ? "" : "s"}`
                : `${totalSeleccionados} de ${totalProductos} seleccionados`}
            </span>
          </div>
        </div>

        {hayDistinta && (
          <div style={styles.avisoDistinta}>
            ⚠️ Elegiste una cotización <strong>distinta a la recomendada por compras</strong>. Al confirmar, la
            que aprobaste reemplaza a la recomendada.
          </div>
        )}

        {totalProductos > 1 && (
          <div style={styles.barraMarcar}>
            <button type="button" onClick={marcarTodos} disabled={procesando} style={styles.botonMarcar}>
              Marcar todos
            </button>
            <button type="button" onClick={quitarTodos} disabled={procesando} style={styles.botonMarcar}>
              Quitar todos
            </button>
          </div>
        )}

        <div style={styles.lista}>
          {grupos.map((g) => {
            const simbolo = simboloMoneda(g.cotizacion.moneda);
            const seleccionados = g.items.filter((it) => cantidades[it.id] > 0);
            // Cotización creada antes de que existiera el precio por producto:
            // no hay forma de saber cuánto vale solo lo marcado, así que se
            // muestra el total completo como referencia, no como monto real.
            const faltaPrecio = seleccionados.some((it) => it.precio == null);
            const subtotal = seleccionados.reduce((s, it) => s + subtotalItem(it), 0);
            // Cada cotización usa el IVA con el que se hizo (si luego cambia el parámetro, las ya hechas no se mueven).
            const ivaPct = Number(g.cotizacion.iva_porcentaje ?? 15);
            const iva = g.cotizacion.exento_iva ? 0 : subtotal * (ivaPct / 100);
            return (
              <div key={g.id} style={styles.grupo}>
                <div style={styles.cabeceraGrupo}>
                  <span style={styles.avatar}>{(g.cotizacion.proveedor_nombre || "?").charAt(0).toUpperCase()}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.proveedor}>{g.cotizacion.proveedor_nombre}</div>
                    <div style={styles.forma}>
                      <BadgeFormaPago valor={g.cotizacion.forma_pago} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={styles.montoEtiqueta}>Total a aprobar</div>
                    <div style={styles.monto}>
                      {faltaPrecio ? (
                        <span title="Esta cotización no tiene precio por producto — se muestra el total completo como referencia.">
                          ≈ {formatoMonto(g.cotizacion)}
                        </span>
                      ) : (
                        dinero(simbolo, subtotal + iva)
                      )}
                    </div>
                  </div>
                </div>

                <div className="table-scroll">
                  <table style={styles.tabla}>
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "1px solid #d3d9df" }}>
                        <th style={{ ...styles.th, width: 34 }}></th>
                        <th style={styles.th}>Producto</th>
                        <th style={{ ...styles.th, textAlign: "right" }}>Pedida</th>
                        <th style={{ ...styles.th, textAlign: "center" }}>A aprobar</th>
                        <th style={{ ...styles.th, textAlign: "right" }}>P. unitario</th>
                        <th style={{ ...styles.th, textAlign: "right" }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((it) => {
                        const cantidadAprobar = cantidades[it.id];
                        const marcado = cantidadAprobar > 0;
                        const esParcial = marcado && cantidadAprobar < it.cantidad;
                        const unidad = it.unidad ? ` ${it.unidad}` : "";
                        return (
                          <tr
                            key={it.id}
                            style={{ borderBottom: "1px solid #eef1f3", opacity: marcado ? 1 : 0.55 }}
                          >
                            <td style={styles.td}>
                              <input
                                type="checkbox"
                                checked={marcado}
                                onChange={() => alternarItem(it.id, it.cantidad)}
                                disabled={procesando}
                              />
                            </td>
                            <td style={{ ...styles.td, lineHeight: 1.3 }}>
                              {it.descripcion}
                              {esParcial && (
                                <div style={styles.avisoParcial}>
                                  Aprobación parcial: {cantidadAprobar} de {it.cantidad}
                                  {unidad}
                                </div>
                              )}
                            </td>
                            <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap", color: "#5b6472" }}>
                              {it.cantidad}
                              {unidad}
                            </td>
                            <td style={{ ...styles.td, textAlign: "center" }}>
                              {it.cantidad > 1 ? (
                                <input
                                  type="number"
                                  min={0}
                                  max={it.cantidad}
                                  value={cantidadAprobar}
                                  onChange={(e) => cambiarCantidad(it.id, e.target.value, it.cantidad)}
                                  disabled={procesando}
                                  style={styles.inputCantidad}
                                  title={`Cantidad a aprobar ahora (de ${it.cantidad}${unidad})`}
                                />
                              ) : (
                                <span style={{ fontWeight: 600 }}>{cantidadAprobar}</span>
                              )}
                            </td>
                            <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                              {it.precio != null ? dinero(simbolo, Number(it.precio) / (it.cantidadBase ?? it.cantidad)) : "—"}
                            </td>
                            <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                              {it.precio != null ? dinero(simbolo, subtotalItem(it)) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!faltaPrecio && (
                  <div style={styles.desglose}>
                    <div style={styles.lineaDesglose}>
                      <span>Subtotal</span>
                      <span>{dinero(simbolo, subtotal)}</span>
                    </div>
                    <div style={styles.lineaDesglose}>
                      <span>{g.cotizacion.exento_iva ? "IVA (exonerado)" : `IVA ${ivaPct}%`}</span>
                      <span>{dinero(simbolo, iva)}</span>
                    </div>
                    <div style={{ ...styles.lineaDesglose, ...styles.lineaTotal }}>
                      <span>Total</span>
                      <span>{dinero(simbolo, subtotal + iva)}</span>
                    </div>
                  </div>
                )}

                {g.recomendada && (
                  <div style={styles.notaRecomendada}>
                    La recomendada por compras era: <strong>{g.recomendada.proveedor_nombre}</strong> ·{" "}
                    {formatoMonto(g.recomendada)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {nota && <p style={styles.nota}>{nota}</p>}

        <div style={styles.pie}>
          <button type="button" onClick={onCancelar} disabled={procesando} style={styles.botonCancelar}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={procesando || totalSeleccionados === 0}
            style={styles.botonConfirmar}
          >
            {procesando
              ? "Aprobando..."
              : totalSeleccionados === 0
              ? "Marca al menos un producto"
              : totalSeleccionados === totalProductos
              ? "✓ Sí, aprobar"
              : `✓ Aprobar ${totalSeleccionados} de ${totalProductos}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(27,42,74,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: "1rem",
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    padding: "1.5rem 1.7rem 1.3rem",
    width: "100%",
    maxWidth: 860,
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 16px 48px rgba(27,42,74,0.35)",
  },
  encabezado: {
    display: "flex",
    gap: "0.9rem",
    alignItems: "center",
    flexWrap: "wrap",
    paddingBottom: "1rem",
    marginBottom: "1rem",
    borderBottom: "1px solid #eef1f3",
  },
  icono: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "#dcf0e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.4rem",
    flexShrink: 0,
  },
  titulo: { margin: 0, fontSize: "1.15rem", color: "var(--tema-titulo, #0b3c60)" },
  subtitulo: { margin: "0.2rem 0 0", fontSize: "0.84rem", color: "#5b6472" },
  resumenChips: { display: "flex", gap: "0.4rem", flexWrap: "wrap" },
  chipFolio: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    borderRadius: 999,
    padding: "0.2rem 0.7rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  chipConteo: {
    background: "#eef1f3",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 999,
    padding: "0.2rem 0.7rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  avisoDistinta: {
    background: "#f2e3d1",
    color: "#7a4a17",
    borderRadius: 10,
    padding: "0.65rem 0.85rem",
    fontSize: "0.83rem",
    marginBottom: "0.9rem",
    lineHeight: 1.4,
  },
  barraMarcar: { display: "flex", gap: "0.5rem", marginBottom: "0.7rem" },
  botonMarcar: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    padding: "0.25rem 0.7rem",
    fontSize: "0.76rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  lista: { display: "flex", flexDirection: "column", gap: "0.9rem" },
  grupo: { border: "1px solid #e2e6ea", borderRadius: 12, overflow: "hidden" },
  cabeceraGrupo: {
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    padding: "0.7rem 0.95rem",
    background: "#f7f9fc",
    borderBottom: "1px solid #eef1f3",
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#2f7a54",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.9rem",
    flexShrink: 0,
  },
  proveedor: { fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", fontSize: "0.95rem" },
  forma: { marginTop: "0.15rem" },
  montoEtiqueta: { fontSize: "0.68rem", fontWeight: 700, color: "#2f7a54", textTransform: "uppercase", letterSpacing: "0.03em" },
  monto: { fontWeight: 700, fontSize: "1.15rem", color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.86rem", color: "var(--tema-titulo, #0b3c60)" },
  th: {
    padding: "0.5rem 0.7rem",
    background: "#eef1f3",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
    whiteSpace: "nowrap",
  },
  td: { padding: "0.55rem 0.7rem", verticalAlign: "middle" },
  inputCantidad: {
    width: 60,
    border: "1px solid #d3d9df",
    borderRadius: 6,
    padding: "0.25rem 0.35rem",
    fontSize: "0.84rem",
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    color: "var(--tema-titulo, #0b3c60)",
  },
  avisoParcial: {
    color: "#a3402f",
    fontWeight: 600,
    fontSize: "0.74rem",
    marginTop: "0.15rem",
  },
  desglose: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "0.2rem",
    padding: "0.7rem 0.95rem",
    background: "#fafbfc",
    borderTop: "1px solid #eef1f3",
    fontSize: "0.84rem",
    color: "#5b6472",
    fontVariantNumeric: "tabular-nums",
  },
  lineaDesglose: { display: "flex", justifyContent: "space-between", gap: "2rem", minWidth: 240 },
  lineaTotal: {
    marginTop: "0.25rem",
    paddingTop: "0.35rem",
    borderTop: "1px solid #d3d9df",
    fontSize: "1rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
  },
  notaRecomendada: {
    padding: "0.5rem 0.95rem",
    background: "#fafbfc",
    borderTop: "1px solid #eef1f3",
    fontSize: "0.78rem",
    color: "#5b6472",
  },
  nota: { fontSize: "0.8rem", color: "#5b6472", margin: "0.8rem 0 0" },
  pie: { display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "1.2rem" },
  botonCancelar: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.55rem 1.1rem",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonConfirmar: {
    background: "#2f7a54",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 1.3rem",
    fontSize: "0.88rem",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default ConfirmarAprobacionModal;
